export const TRAITS: Array<string> = [
	"Mobile Transfer",
	"Aisle Seats",
	"Obstructed View",
	"No View",
	"Side View",
	"Limited View",
	"Partial View",
	"Rear View",
	"AXS Mobile ID",
	"Wheelchair Accessible",
	"Parking Pass Only",
	"Paperless Tickets",
	"Standing Room Only",
	"Local Pickup Only",
	"Will Call Only",
	"21+ Event",
	"Vaccination Required",
	"Barstool Seating",
];

export const TRAIT_ALIASES: Array<{ trait: string; alias: string }> = [{ trait: "Mobile Transfer", alias: "Mobile XFER" }];

export const TRAIT_STORED_VALUE: Record<string, string> = {
	"Mobile Transfer": "Mobile XFER",
};

export const normalizeTrait = (value: string) => value.trim().toLowerCase().replace(/\s+/g, " ");

type TraitToken = { words: Array<string>; canonical: string };

const buildTraitTokens = (): Array<TraitToken> => {
	const tokens: Array<TraitToken> = [];
	TRAITS.forEach((t) => tokens.push({ words: t.split(/\s+/).map(normalizeTrait), canonical: t }));
	TRAIT_ALIASES.forEach(({ trait, alias }) =>
		tokens.push({ words: alias.split(/\s+/).map(normalizeTrait), canonical: trait }),
	);
	return tokens.sort((a, b) => b.words.length - a.words.length);
};

const TRAIT_TOKENS = buildTraitTokens();

export type ParsedExternalNotes = {
	traits: Array<string>;
	leftovers: Array<string>;
};

// External_Notes can come back either comma/semicolon separated (eventInventoryAll)
// or as plain space-separated text (queryInventory in Global Inventory). This walks
// the stream greedy-longest-first against the known trait list (+ aliases) so both
// formats yield the same trait set.
export const parseExternalNotes = (raw: string | null | undefined): ParsedExternalNotes => {
	if (!raw) return { traits: [], leftovers: [] };

	const words = raw
		.replace(/[,;]/g, " ")
		.split(/\s+/)
		.filter(Boolean);

	if (!words.length) return { traits: [], leftovers: [] };

	const lowered = words.map(normalizeTrait);
	const consumed = new Array(words.length).fill(false);
	const traits = new Set<string>();

	for (let i = 0; i < words.length; i++) {
		if (consumed[i]) continue;
		for (const token of TRAIT_TOKENS) {
			const end = i + token.words.length;
			if (end > words.length) continue;
			let isMatch = true;
			for (let k = 0; k < token.words.length; k++) {
				if (consumed[i + k] || lowered[i + k] !== token.words[k]) {
					isMatch = false;
					break;
				}
			}
			if (isMatch) {
				traits.add(token.canonical);
				for (let k = i; k < end; k++) consumed[k] = true;
				break;
			}
		}
	}

	const leftovers: Array<string> = [];
	let buffer: Array<string> = [];
	for (let i = 0; i < words.length; i++) {
		if (consumed[i]) {
			if (buffer.length) {
				leftovers.push(buffer.join(" "));
				buffer = [];
			}
		} else {
			buffer.push(words[i]);
		}
	}
	if (buffer.length) leftovers.push(buffer.join(" "));

	return {
		traits: TRAITS.filter((t) => traits.has(t)),
		leftovers,
	};
};

export const isKnownTraitOrAlias = (value: string): boolean => {
	const n = normalizeTrait(value);
	for (const token of TRAIT_TOKENS) {
		if (token.words.join(" ") === n) return true;
	}
	return false;
};
